/**
 * Program: Germany
 * Language: English
 * Content: Terms & Conditions
 */

// eslint-disable-next-line import/prefer-default-export
const terms = `
# Terms and Conditions for the Dyson Trade In Service

These terms and conditions govern the Dyson Trade In Service, which is provided by Alchemy Telco Solutions Limited, Monasterevin Road, Kildare Town, Kildare R51 A262, Ireland ("Vendor" "us", "our" or "we") for and on behalf of Dyson Austria GmbH Rothschildplatz 3, 6.OG, 1020 Wien (referred to as „Dyson“).

By contracting the Trade In Service with us, you (the “Customer”) can get a cash payment when buying a specific new Dyson device from a participating retailer during the offer period and trading in an eligible product to the Vendor during the trade-in period, all as further detailed below.

1. ### Who can Contract the Trade In Service; Exceptions

    You must be eighteen (18) years old or older. Contracting the Trade in Service is not open for resellers of Dyson products and their employees and is not available for purchases completed using Dyson Employee Codes and/or Benefits.

2. ### How to Contract the Trade In Service

    In order to contract the Trade In Service you must do the following and comply with the following requirements:

    1. You must have purchased a new Dyson product of the following models:

        - V15s Detect Submarine™
        - Gen5detect™ Absolute
        - V16 Piston Animal
        - V16 Piston Animal Submarine™
        - Airwrap Co-anda2x™
        - Airwrap i.d.™ (selected models)

        (each a “**New Dyson Device**”).

    2. The purchase of the New Dyson Device must have occurred between 15 September 2025 and 31 October 2025 (both inclusive) (“**Offer Period**”). You must not have returned your New Dyson Device for a refund during the allowable return period after purchase.

    3. The purchase of the New Dyson Device must have been made at one of the following retailers, either online or in a physical store in Austria:

        - Dyson.at or a Dyson Demo store in the following locations: Linz/Pasching, Parndorf, Vienna SCS, Vienna Steffl
        - any indirect Dyson retail partner from this list: Media Markt, Saturn, QVC, Expert, Euronics, MediMax, Red Zac, Electronic Partner, Electronic4you, 0815, Otto, Universal, Kastner & Öhler, Metro, Metro Markets
        - official Dyson Austria shop on Ebay & Otto

        This promotion cannot be used in conjunction with any other promo from the Dyson website [(http://dyson.at)](http://dyson.at).

    4. After the purchase of the New Dyson Device, you must complete a registration form through the Dyson website within 14 calendar days from purchase and provide the following details:

        - name and postal address, email address and telephone number
        - bank account details of a bank in Austria, or another country within the European Economic Area to which payments can be made, if applicable, for both programs.

        Moreover, you must upload a copy of the purchase receipt of the New Dyson Device and a photo of the serial number of the unpacked New Dyson Device and provide details of the Eligible Trade In Device (as defined below). You can only sign up for the Trade in Service once for a given New Dyson Device.

    5. Upon completion of the registration form, you will receive a prepaid freight label to send your trade-in product for trade-in to us. The trade-in product can be any floor vacuum cleaner, notwithstanding its brand or model and must be intact (in one piece, not broken in pieces or shattered) and the dust collection receptor or bag must be empty **(“Eligible Trade In Device”**). Wands, hoses, cables, batteries or any other accessories are not required.

    6. Thereafter, you must send your Eligible Trade In Device to us within 14 calendar days from completion of the registration form **(“Trade In Period”**) by dropping it at a DHL collection center in Austria. The freight is for the cost of the Vendor and is only related to this specific Eligible Trade In Device.

3. ### Payment for Eligible Trade In Device

    Upon confirmation of receipt of the Eligible Trade In Device, together with the required details from the registration form, based on the Eligible Trade In Device you send in, we will pay to you the following amounts:

    - V15s Detect Submarine™: €200
    - Gen5detect™ Absolute: €200
    - V16 Piston Animal: €150
    - V16 Piston Animal Submarine™: €150
    - V16 Piston Animal + Detailed Cleaning Kit: €150
    - V16 Piston Animal Submarine™ (Soft Bundle): €150
    - Airwrap Co-anda2x™, Airwrap i.d.™ (selected models): €100 (with a traded-in Dyson beauty device), €75 (with a traded-in beauty device of another brand)

    You will be sent confirmation of the applicable payment amount to the email address communicated to us. We will arrange for payment no earlier than 30 days and in any case within 45 days after receipt of a valid Eligible Trade In Device. All quotations and payments include VAT (as applicable).

    The payment will be made by bank transfer to bank account details provided by you. You are responsible for providing accurate banking details to us and we are not responsible in the event incorrect banking details are provided.

    In the event the device traded in is not eligible (i.e., not a Floorcare or Beauty product or your New Dyson Device was returned for a refund), no payment will be made. We will contact you to let you know, but unfortunately, we will not be able to return your item back to you.

4. ### Ownership transfers to Vendor

    Upon confirmation by email from the Vendor of receipt of the Eligible Trade In Device and its eligibility for the Trade In Service, title of ownership in such Eligible Trade In Device transfers to Vendor from the Customer and Customer disclaims any further right, title or interest in and to the Eligible Trade In Device or any items contained therein.

5. ### Customer Declarations

    You declare that:

    - you are the sole owner of the Eligible Trade In Device or have been authorized by the owner of the Eligible Trade In Device and that you are able to make decisions with regards to the Eligible Trade In Device,
    - there are no liens, encumbrances, or security interests in or attached to the Eligible Trade In Device and that no other party has a legal interest in it,
    - any items you seek to trade-in through the Trade in Service shall not infringe on any third-party intellectual property right (including copyright, trademarks, patent, trade secrets or other proprietary right),
    - you are not and do not intend to become a reseller of Dyson products; and
    - the Eligible Trade In Device is not counterfeit, stolen or fraudulent.

    More generally, the Customer warrants that the Eligible Trade In Device does not infringe any third-party rights and hereby indemnifies the Vendor against any action or claim based on the fact that the ownership or use of the Eligible Trade In Device by the Vendor infringes any property right held by a third party, and assumes all liability, loss, costs, damages, and expenses that may result directly or indirectly from such claims.

6. ### Limitation of Liability

    The liability of the Vendor for a breach of contractual and non-contractual duties is determined by the pertinent statutory provisions.

    Regardless of the basis in law, the Vendor is liable to compensate for damages only for intent and gross negligence. In the event of simple negligence, the Vendor is only liable

    1. for damages arising from death or personal injury,
    2. for damages arising from the breach of a fundamental contractual duty; in this event, however, the liability of the Vendor to compensate for damages is limited to the foreseeable damages that would typically have occurred. Deemed to be a fundamental contractual duty for this purpose is an obligation without which proper performance of the contract would be impossible and on the performance of which the other party relies and is entitled to rely.

    All other claims of the Customer based on contract or tort are excluded.

7. ### Online Dispute Resolution

    The European Commission provides a platform for out-of-court online dispute resolution (OS platform), available at [https://ec.europa.eu/consumers/odr](https://ec.europa.eu/consumers/odr). We are not obliged and not willing to participate in dispute resolution proceedings before consumer arbitration bodies.

8. ### Communications between us

    You may contact the Vendor’s customer service team at Email: dyson@wearealchemy.com. If we need to contact you or give you notice in writing, we will do so by e-mail to the address you provide to us in your completed form.

9. ### Data Protection

    Regarding Alchemy's use of your personal information the following Privacy Information applies:

    **Privacy Information for the Dyson Trade in Service**

    Alchemy takes your privacy very seriously. Please read this privacy information carefully as it contains important information about who we are, and how and why we collect, store, use and share your personal data. It also explains your rights in relation to your personal data and how to contact us or the supervisory authorities in the event you have a complaint.

    We collect, process and are responsible for certain personal data about you. When we do so, we are subject to the applicable data protection laws and are responsible as data controller of that personal data for the purposes of those laws.

    1. **Who is the responsible controller of personal data / contact details**

        The responsible controller in terms of data protection legal requirements is

        Alchemy Telco Solutions Ltd.
        Monasterevin Road,
        Kildare Town,
        Kildare R51 A262,
        Ireland

        (hereinafter referred to as „we“ or „us“).

        If you have a concern regarding our processing of personal data or you wish to exercise your rights as a data subject, you may contact our Data Protection Officer via [privacy@wearealchemy.com](mailto:privacy@wearealchemy.com).

    2. **What personal data we process**

        We collect and process the following personal data about you when you submit your details using the Dyson Trade in Service:

        - your name and contact information, including postal address, email address and telephone number,
        - your billing information, transaction and payment information (including bank account information),
        - information about your New Dyson Device and the respective purchase (pictures of the purchase receipt unpacked and the New Dyson Device and its serial number),
        - information about your Trade in Device (including a serial number, in case the Trade in Device is a Dyson Device)

    3. **For which purpose do we process your personal data**

        Your contact and billing information as well as the information about your New Dyson Device and your Eligible Trade In Device is exclusively used by us to provide the Trade in Service for and on behalf of Dyson Austria GmbH (hereinafter referred to as „Dyson“), to contact you in the course of the Trade in Service and to carry out payments to you for your Eligible Trade In Device.

    4. **With whom do we share your data**

        We share the serial number of the New Dyson Device as well as the serial number of a Trade in Device that is a Dyson Device with Dyson, who will process this information for own purposes (which may include to retire a Dyson Trade In Device from future support).

        In order to technically provide the Trade In Service, we use professional processors who carry out specific services for us, such as hosting and software services. All processors are bound by contractual measures according to the legal requirements.

        When we share data with recipients outside of the EU, we adhere to the legal requirements to establish an adequate level of data protection at the recipient. Measures include the use of standard contractual clauses, issued by the European Commission, and of which you may request a copy from us by contacting us any time.

    5. **Legal basis for processing your personal data**

        The legal basis for all processing of your personal data in the course of the Trade In Service is Art. 6 para 1 lit. b) and f) GDPR. Our legitimate interest is our business interest in providing you with the Trade In Service on behalf of Dyson.

    6. **Retention of personal data**

        We store your personal data collected in the course of the Dyson Trade In Service as described in this Privacy Notice for as long as you use our Services, or as necessary to fulfill the purpose of the Trade In Service, including to resolve disputes, establish legal defenses, pursue legitimate business purposes, enforce our agreements, and comply with applicable laws and mandatory retention requirements.

    7. **What legal rights do I have**

        Subject to legal requirements, you have the right to demand from us free of charge:

        - information about the stored personal data concerning you (Art. 15 GDPR),
        - correction (Art. 16 GDPR), deletion (Art. 17 GDPR), restriction of processing (Art. 18 GDPR)
        - transfer of your data to a third party (Art. 20 GDPR).

        You furthermore have at any time the unconditional right to lodge a complaint to a competent data protection supervisory authority, which may be the supervisory authority of your habitual residence (Art. 77 GDPR)
`

export default terms
